/**
 * One product page per Featured brands slug so a popular brand can't crowd
 * out covers for the other three tiles on a shared multi-brand page.
 */

import { useMemo } from "react";
import { useQuery } from "react-query";
import {
  fetchTemplateShopProductsRpcPage,
  type ShopCatalogProduct,
} from "../templateShopCatalog";
import type { FeaturedBandImage } from "./featuredBandTypes";
import { productsForBrand, type ShopBrandOption } from "../shopPageMerch";

const COVER_LIMIT = 4;

export function useFeaturedBrandCovers(args: {
  username: string;
  userId: string;
  enabled: boolean;
  slugs: string[];
}): {
  coverBySlug: Map<string, string>;
  products: ShopCatalogProduct[];
  loading: boolean;
} {
  const { username, userId, enabled } = args;
  const slugs = useMemo(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of args.slugs) {
      const slug = String(raw || "").trim().toLowerCase();
      if (!slug || seen.has(slug)) continue;
      seen.add(slug);
      out.push(slug);
    }
    return out.slice(0, 4);
  }, [args.slugs]);

  const slugsKey = slugs.join("+");
  const queryEnabled =
    enabled && Boolean(username && userId) && slugs.length > 0;

  const query = useQuery(
    ["featured-brand-covers-v1", userId, username, slugsKey],
    async () => {
      const pages = await Promise.all(
        slugs.map((slug) =>
          fetchTemplateShopProductsRpcPage({
            username,
            userId,
            offset: 0,
            limit: COVER_LIMIT,
            brandSlugs: [slug],
            includeFacets: false,
          }),
        ),
      );
      const coverBySlug = new Map<string, string>();
      const products: ShopCatalogProduct[] = [];
      const seenIds = new Set<string>();
      slugs.forEach((slug, i) => {
        for (const p of pages[i]?.products || []) {
          const id = String(p.postId || "").trim();
          if (id && !seenIds.has(id)) {
            seenIds.add(id);
            products.push(p);
          }
          const img = String(p.image || "").trim();
          if (img && !coverBySlug.has(slug)) coverBySlug.set(slug, img);
        }
      });
      return { coverBySlug, products };
    },
    {
      enabled: queryEnabled,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      // Keep prior covers while brand slugs change — avoid band skeleton /
      // Products catalog re-gate (felt like a full page reload).
      keepPreviousData: true,
    },
  );

  // Initial load only — any prior cover payload keeps the band painted while
  // a new slug set fetches (keepPreviousData).
  const loading = Boolean(queryEnabled && !query.data);

  return {
    coverBySlug: query.data?.coverBySlug ?? new Map(),
    products: query.data?.products ?? [],
    loading,
  };
}

/** Build the 2×2 Featured brands cells — index-aligned to selected slugs. */
export function buildFeaturedBrandImages(args: {
  slugs: string[];
  brands: ShopBrandOption[];
  productPool: ShopCatalogProduct[];
  coverBySlug?: Map<string, string> | null;
}): FeaturedBandImage[] {
  const slugs = args.slugs.slice(0, 4);
  return Array.from({ length: 4 }, (_, j) => {
    const slug = String(slugs[j] || "").trim().toLowerCase();
    const brand = slug
      ? args.brands.find((b) => b.slug === slug)
      : undefined;
    const label = String(brand?.label || "").trim();
    const fromProducts = slug
      ? productsForBrand(args.productPool, slug)
      : [];
    const cover = String(args.coverBySlug?.get(slug) || "").trim();
    // Dedicated brand-page cover wins. `brand.images[0]` can be a facet or
    // unfiltered-catalog sample that arrives earlier and would swap out.
    const src = String(
      cover ||
        fromProducts.find((p) => String(p.image || "").trim())?.image ||
        brand?.images?.[0] ||
        "",
    ).trim();
    return {
      src,
      alt: label || "Brand",
      label,
      brandSlug: slug,
      fit: "cover" as const,
    };
  });
}
